// Per-field manual slip review for ML readings in fraud-suspect / review state
// (EP-105 D4-revised; EP-143: parking auto-verifies when all gates pass, so
// this checklist is shown only when a slip routes to manual review).
// Rendered inside the Verification card's ML Reading Detail section, two parts:
//
// - COMPARE rows (amount, datetime): the payment already carries the expected
//   value, so the row pairs the OCR read with it and — when onToggleField is
//   provided (admin/owner) — offers a Check button whose ticked keys flow back
//   to the parent (prefilling the Verify modal's reason).
// - CORRECTION rows (bank, sender, receiver, trans_ref, ref1): no ground truth
//   exists on the payment, so instead of a check the operator can enter the
//   value as printed on the slip. Only explicitly entered values are saved to
//   slip_metadata.ml_corrections (EP-101 label capture) — they become training
//   labels joinable to the slip image via the reading_id.
//
// Neither part ever writes verification state.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Button, Input, Select } from '@/components/ui'
import { formatBaht } from '@/lib/money'
import { formatDate as formatDateWith } from '@/lib/date'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useSaveSlipCorrections } from '@/hooks/usePayments'
import { useNotificationStore } from '@/stores/useNotificationStore'
import type { Payment, SlipReadingOpinion } from '@/types'

// Mirror of ml/app/stages/slip_bank_classifier.py BANK_CODES (and the server
// allowlist in PaymentsController) — the correction dropdown offers exactly
// the classifier's class vocabulary.
const BANK_CODES = [
  'scb',
  'kbank',
  'ktb',
  'bbl',
  'bay',
  'ttb',
  'gsb',
  'kkp',
  'promptpay_generic',
  'unknown',
] as const

export interface SlipChecklistRow {
  key: string
  label: string
  ocr?: string | null
  expected?: string | null
  confidence?: number
}

// Compare rows only (amount, datetime) — the fields whose expected value the
// payment already carries. Exported so the page can translate checked field
// keys into the Verify modal's prefilled reason using the same labels.
// eslint-disable-next-line react-refresh/only-export-components -- helper co-located with its component; consumed by the payment detail page
export function buildChecklistRows(
  payment: Payment,
  mlReading: SlipReadingOpinion | undefined,
  t: (key: string) => string,
  // Account-aware by default the caller passes useDateFormatter(); the pure
  // lib formatter (no account timezone/format) is only a fallback for callers
  // outside a component (e.g. tests exercising this helper directly).
  formatDate: (dateStr: string | null | undefined) => string = formatDateWith,
): SlipChecklistRow[] {
  const fields = mlReading?.fields ?? {}
  const rows: SlipChecklistRow[] = [
    {
      key: 'amount',
      label: t('verification.field_amount'),
      ocr: fields.amount?.value,
      expected: payment.amount != null ? formatBaht(payment.amount, { locale: 'en', decimals: 2 }) : undefined,
      confidence: fields.amount?.confidence,
    },
    {
      key: 'datetime',
      label: t('verification.field_datetime'),
      ocr: fields.datetime?.value,
      expected: formatDate(payment.paid_at),
      confidence: fields.datetime?.confidence,
    },
  ]
  return rows.filter((r) => (r.ocr != null && r.ocr !== '') || (r.expected != null && r.expected !== ''))
}

interface CorrectionRow {
  key: string
  label: string
  /** What the ML read (display form) — shown as reference and used as the input placeholder. */
  mlValue?: string | null
  confidence?: number
  /** Known reference value (e.g. the parking promptpay_ref1) shown for context. */
  expected?: string | null
}

function buildCorrectionRows(
  payment: Payment,
  mlReading: SlipReadingOpinion | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): CorrectionRow[] {
  const fields = mlReading?.fields ?? {}
  return [
    {
      key: 'bank_code',
      label: t('verification.field_bank'),
      mlValue: mlReading?.bank_code
        ? t(`verification.banks.${mlReading.bank_code}`)
        : undefined,
      confidence: mlReading?.bank_confidence,
    },
    {
      key: 'sender_name',
      label: t('verification.field_sender'),
      mlValue: fields.sender_name?.value,
      confidence: fields.sender_name?.confidence,
    },
    {
      key: 'receiver_name',
      label: t('verification.field_receiver'),
      mlValue: fields.receiver_name?.value,
      confidence: fields.receiver_name?.confidence,
    },
    {
      key: 'trans_ref',
      label: t('verification.field_trans_ref'),
      mlValue: fields.trans_ref?.value ?? mlReading?.qr?.trans_ref,
      confidence: fields.trans_ref?.confidence,
    },
    {
      key: 'ref1',
      label: t('verification.field_ref1'),
      mlValue: mlReading?.qr?.ref1,
      expected: payment.metadata?.promptpay_ref1,
    },
  ]
}

interface SlipFieldChecklistProps {
  payment: Payment
  mlReading?: SlipReadingOpinion
  checkedFields?: string[]
  /** When absent (non-admin), everything renders read-only: no check buttons, no correction inputs. */
  onToggleField?: (fieldKey: string) => void
}

export function SlipFieldChecklist({
  payment,
  mlReading,
  checkedFields = [],
  onToggleField,
}: SlipFieldChecklistProps) {
  const { t } = useTranslation('payments')
  const addToast = useNotificationStore((s) => s.addToast)
  const saveCorrections = useSaveSlipCorrections()
  const formatDate = useDateFormatter()

  const savedFields: Record<string, string> = payment.slip_metadata?.ml_corrections?.fields ?? {}
  const [corrections, setCorrections] = useState<Record<string, string>>(savedFields)

  const canEdit = !!onToggleField
  const compareRows = buildChecklistRows(payment, mlReading, t, formatDate)
  // Read-only viewers only see correction rows that carry a value; editors get
  // all rows so they can also supply values the ML missed entirely.
  const correctionRows = buildCorrectionRows(payment, mlReading, t).filter(
    (r) => canEdit || r.mlValue || r.expected || savedFields[r.key]
  )

  if (compareRows.length === 0 && correctionRows.length === 0) return null

  // Only explicitly entered values that differ from what is already saved are
  // submitted — the server merges them per-field into ml_corrections.fields.
  const changed: Record<string, string> = {}
  for (const [key, raw] of Object.entries(corrections)) {
    const value = raw.trim()
    if (value && value !== (savedFields[key] ?? '')) changed[key] = value
  }
  const dirty = Object.keys(changed).length > 0

  const handleSave = async () => {
    await saveCorrections.mutateAsync({ id: payment.id, corrections: changed })
    addToast({ type: 'success', message: t('verification.corrections_saved') })
  }

  const setField = (key: string, value: string) =>
    setCorrections((prev) => ({ ...prev, [key]: value }))

  return (
    <div>
      {canEdit && compareRows.length > 0 && (
        <p className="text-xs text-base-content/60 mb-2">{t('verification.manual_check_hint')}</p>
      )}
      <div className="space-y-2">
        {compareRows.map((row) => {
          const checked = checkedFields.includes(row.key)
          return (
            <div
              key={row.key}
              className={`flex items-center gap-2 rounded-lg border p-2 ${
                checked ? 'border-success bg-success/5' : 'border-base-200'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  {row.label}
                  {row.confidence !== undefined && (
                    <span className="ml-2 font-mono text-xs text-base-content/50">
                      {(row.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-base-content/60">
                  {t('verification.ocr_value')}:{' '}
                  <span className="font-mono break-all">{row.ocr || '—'}</span>
                </div>
                {row.expected && (
                  <div className="text-xs text-base-content/60">
                    {t('verification.expected_value')}:{' '}
                    <span className="font-mono break-all">{row.expected}</span>
                  </div>
                )}
              </div>
              {onToggleField && (
                <Button
                  size="sm"
                  variant={checked ? 'success' : 'outline'}
                  leftIcon={<Check className="w-3.5 h-3.5" />}
                  onClick={() => onToggleField(row.key)}
                  aria-pressed={checked}
                >
                  {checked ? t('verification.checked_field') : t('verification.check_field')}
                </Button>
              )}
            </div>
          )
        })}
      </div>

      {correctionRows.length > 0 && (
        <div className="mt-3">
          {canEdit && (
            <p className="text-xs text-base-content/60 mb-2">{t('verification.corrections_hint')}</p>
          )}
          <div className="space-y-2">
            {correctionRows.map((row) => (
              <div key={row.key} className="rounded-lg border border-base-200 p-2">
                <div className="text-sm font-medium">
                  {row.label}
                  {row.confidence !== undefined && (
                    <span className="ml-2 font-mono text-xs text-base-content/50">
                      {(row.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-base-content/60">
                  {t('verification.ocr_value')}:{' '}
                  <span className="font-mono break-all">{row.mlValue || '—'}</span>
                </div>
                {row.expected && (
                  <div className="text-xs text-base-content/60">
                    {t('verification.expected_value')}:{' '}
                    <span className="font-mono break-all">{row.expected}</span>
                  </div>
                )}
                {canEdit &&
                  (row.key === 'bank_code' ? (
                    <Select
                      className="mt-1"
                      aria-label={row.label}
                      options={BANK_CODES.map((code) => ({
                        value: code,
                        label: t(`verification.banks.${code}`),
                      }))}
                      placeholder={t('verification.correction_placeholder')}
                      value={corrections.bank_code ?? ''}
                      onChange={(e) => setField('bank_code', e.target.value)}
                    />
                  ) : (
                    <Input
                      className="mt-1"
                      aria-label={row.label}
                      placeholder={row.mlValue ?? t('verification.correction_placeholder')}
                      value={corrections[row.key] ?? ''}
                      onChange={(e) => setField(row.key, e.target.value)}
                    />
                  ))}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty}
                loading={saveCorrections.isPending}
              >
                {t('verification.save_corrections')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
